(() => {
  if (!('customElements' in window) || customElements.get('carisma-faq-page')) return;

  class CarismaFaqPage extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        if (this.isConnected && !this.controller) this.initialize();
      });
    }

    initialize() {
      this.controller = new AbortController();
      this.cards = [...this.querySelectorAll('[data-cfp-card]')];
      this.filters = [...this.querySelectorAll('[data-cfp-filter]')];
      this.empty = this.querySelector('[data-cfp-empty]');
      this.count = this.querySelector('[data-cfp-count]');
      this.dialog = this.querySelector('[data-cfp-dialog]');
      this.player = this.querySelector('[data-cfp-player]');
      this.dialogTitle = this.querySelector('[data-cfp-modal-title]');
      this.sourceLink = this.querySelector('[data-cfp-original-link]');
      this.defaultTitle = this.dialogTitle?.textContent || '';
      const on = (target, type, callback) => {
        target?.addEventListener(type, callback, { signal: this.controller.signal });
      };

      this.filters.forEach((button) => {
        on(button, 'click', () => this.filterCards(button.dataset.cfpFilter));
      });
      this.filterCards('all');
      const filterBar = this.querySelector('[data-cfp-filters]');
      if (filterBar) filterBar.hidden = false;

      const templates = [...this.querySelectorAll('template')];
      this.querySelectorAll('[data-cfp-video]').forEach((button) => {
        const template = templates.find((item) => item.id === button.dataset.videoTemplate);
        const media = template?.content.querySelector('video')
          ? { kind: 'hosted', template }
          : this.parseUrl(button.dataset.videoUrl);
        button.hidden = !media;
        if (media) on(button, 'click', () => this.openVideo(button, media));
      });
      on(this.querySelector('[data-cfp-close]'), 'click', () => this.closeVideo());
      on(this.dialog, 'cancel', (event) => {
        event.preventDefault();
        this.closeVideo();
      });
      on(this.dialog, 'close', () => {
        if (!this.dialog.open) this.closeVideo(false);
      });
      on(this.dialog, 'click', (event) => {
        if (event.target !== this.dialog) return;
        const bounds = this.dialog.getBoundingClientRect();
        if (event.clientX < bounds.left || event.clientX > bounds.right ||
            event.clientY < bounds.top || event.clientY > bounds.bottom) this.closeVideo();
      });
      on(document, 'shopify:block:select', (event) => {
        if (!(event.target instanceof Element) || !this.contains(event.target)) return;
        const card = event.target.closest('[data-cfp-card]') || event.target.querySelector('[data-cfp-card]');
        if (card && this.cards.includes(card)) this.filterCards('all');
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.closeVideo();
    }

    filterCards(category) {
      const selected = category || 'all';
      let visible = 0;
      this.cards.forEach((card) => {
        const matches = selected === 'all' || (card.dataset.categories || '').includes(`|${selected}|`);
        card.hidden = !matches;
        if (matches) visible += 1;
      });
      this.filters.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.cfpFilter === selected));
      });
      if (this.empty) this.empty.hidden = visible !== 0;
      if (this.count) this.count.textContent = (this.dataset.resultsLabel || '{count} answers').replace(/\{count\}/g, String(visible));
    }

    parseUrl(value) {
      const input = String(value || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().replace(/&amp;/gi, '&');
      if (!input || /[\s<>\\\u0000-\u001f\u007f]/.test(input) || /^\/(?!\/)/.test(input)) return null;
      if (/^[a-z][a-z\d+.-]*:/i.test(input) && !/^https?:\/\//i.test(input)) return null;
      try {
        const address = input.startsWith('//') ? `https:${input}` : /^https?:\/\//i.test(input) ? input : `https://${input}`;
        const url = new URL(address);
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password || !url.hostname.includes('.')) return null;
        const host = url.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
        const parts = url.pathname.split('/').filter(Boolean);
        if (['youtube.com', 'youtube-nocookie.com', 'music.youtube.com', 'youtu.be'].includes(host)) {
          const id = host === 'youtu.be' ? parts[0]
            : ['shorts', 'embed', 'live', 'v'].includes(parts[0]) ? parts[1]
              : parts[0] === 'watch' ? url.searchParams.get('v') : null;
          if (!/^[A-Za-z0-9_-]{11}$/.test(id || '')) return null;
          const params = new URLSearchParams({ autoplay: '1', playsinline: '1', rel: '0' });
          const time = url.searchParams.get('start') || url.searchParams.get('t') || '';
          const match = time.match(/^(?:(\d+)h)?(?:(\d+)m)?(?:(\d+)s?)?$/);
          const seconds = match ? Number(match[1] || 0) * 3600 + Number(match[2] || 0) * 60 + Number(match[3] || 0) : 0;
          if (Number.isSafeInteger(seconds) && seconds > 0) params.set('start', String(seconds));
          url.protocol = 'https:';
          return { kind: 'youtube', source: url.href, embed: `https://www.youtube-nocookie.com/embed/${id}?${params}` };
        }
        if (['vimeo.com', 'player.vimeo.com'].includes(host)) {
          const match = url.pathname.match(/^\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|showcase\/\d+\/video\/)?(\d+)(?:\/([A-Za-z0-9]+))?\/?$/);
          if (!match) return null;
          const params = new URLSearchParams({ autoplay: '1', playsinline: '1' });
          const hash = url.searchParams.get('h') || match[2];
          if (hash && !/^[A-Za-z0-9]+$/.test(hash)) return null;
          if (hash) params.set('h', hash);
          url.protocol = 'https:';
          return { kind: 'vimeo', source: url.href, embed: `https://player.vimeo.com/video/${match[1]}?${params}` };
        }
        if (host === 'instagram.com') {
          if (!['reel', 'reels', 'p', 'tv'].includes(parts[0]) || !/^[A-Za-z0-9_-]+$/.test(parts[1] || '') || parts.length !== 2) return null;
          const route = parts[0] === 'reels' ? 'reel' : parts[0];
          url.protocol = 'https:';
          return { kind: 'instagram', source: url.href, embed: `https://www.instagram.com/${route}/${parts[1]}/embed/` };
        }
        if (url.protocol !== 'https:') return null;
        return { kind: /\.(mp4|webm)$/i.test(url.pathname) ? 'file' : 'external', source: url.href };
      } catch (_) {
        return null;
      }
    }

    hostedSource(template) {
      const video = template.content.querySelector('video');
      const media = this.parseUrl(video?.getAttribute('src') || video?.querySelector('source[src]')?.getAttribute('src'));
      return media?.source || null;
    }

    openVideo(button, media) {
      if (this.dialog?.open) return;
      const source = media.kind === 'hosted' ? this.hostedSource(media.template) : media.source;
      if (!this.dialog || !this.player || typeof this.dialog.showModal !== 'function') {
        if (source) window.location.assign(source);
        return;
      }
      CarismaFaqPage.active?.closeVideo();
      this.opener = button;
      const title = button.dataset.videoTitle || button.getAttribute('aria-label') || this.defaultTitle;
      if (this.dialogTitle) this.dialogTitle.textContent = title;
      this.dialog.dataset.provider = media.kind;
      this.player.hidden = media.kind === 'external';
      if (this.sourceLink) {
        this.sourceLink.hidden = !source;
        if (source) this.sourceLink.href = source;
        else this.sourceLink.removeAttribute('href');
      }
      try {
        this.dialog.showModal();
      } catch (_) {
        this.closeVideo();
        if (source) window.location.assign(source);
        return;
      }
      CarismaFaqPage.active = this;
      let video;
      if (media.kind === 'hosted') {
        this.player.append(media.template.content.cloneNode(true));
        video = this.player.querySelector('video');
      } else if (media.kind === 'file') {
        video = document.createElement('video');
        video.src = source;
        this.player.append(video);
      } else if (media.embed) {
        const iframe = document.createElement('iframe');
        iframe.src = media.embed;
        iframe.title = title;
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        this.player.append(iframe);
      }
      if (video) {
        video.controls = true;
        video.playsInline = true;
        video.preload = 'none';
        video.removeAttribute('autoplay');
        if (title) video.setAttribute('aria-label', title);
        video.play()?.then(() => {
          if (!this.dialog.open || !this.player.contains(video)) video.pause();
        }).catch(() => {});
      }
      this.querySelector('[data-cfp-close]')?.focus({ preventScroll: true });
    }

    closeVideo(closeDialog = true) {
      const opener = this.opener;
      this.opener = null;
      this.player?.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.removeAttribute('src');
        video.querySelectorAll('source').forEach((source) => source.remove());
        video.load();
      });
      this.player?.replaceChildren();
      if (this.dialog) {
        if (closeDialog && this.dialog.open) this.dialog.close();
        delete this.dialog.dataset.provider;
      }
      if (this.sourceLink) {
        this.sourceLink.hidden = true;
        this.sourceLink.removeAttribute('href');
      }
      if (this.dialogTitle) this.dialogTitle.textContent = this.defaultTitle;
      if (CarismaFaqPage.active === this) CarismaFaqPage.active = null;
      if (this.isConnected && opener?.isConnected) opener.focus({ preventScroll: true });
    }
  }

  customElements.define('carisma-faq-page', CarismaFaqPage);
})();
